import React, { useMemo } from 'react';

import { reportException, SentryTags } from '@gitbook/util-debug';
import { isValidDate } from '@gitbook/util-format';
import { useDateRefresh } from '@gitbook/util-react-hooks';

import { toLocaleDateTimeString } from '../Intl';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 31 * DAY;
const YEAR = 365 * DAY;

/**
 * Hook to use a relative date. Handles SSR refreshing and any locale management.
 */
export function useDateShortRelative(
    input: Date | string | number,
    options: { onlyDate?: boolean; prefix?: string } = { onlyDate: true }
): {
    /** The date value */
    date: Date;
    /** A human-readable relative value for the date */
    value: string;
    /** An accessible value for the date */
    accessibilityValue: string;
    /** A tooltip for the date */
    tooltip: string;
} {
    const { onlyDate = true, prefix = '' } = options;

    const value = React.useMemo(() => new Date(input), [input]);
    const isValidValue = isValidDate(value);
    const invalidInput = isValidValue ? null : getInvalidDateInput(input);
    const invalidInputType = invalidInput?.type;
    const invalidInputValue = invalidInput?.value;

    useDateRefresh(isValidValue ? value : null);

    React.useEffect(() => {
        if (!invalidInputType || invalidInputValue === undefined) {
            return;
        }

        reportException(new Error('Invalid date passed to DateShortRelative'), {
            level: 'warning',
            tags: {
                [SentryTags.ERROR_CATEGORY]: 'invalid-date-short-relative',
                date_input_type: invalidInputType,
            },
            extra: {
                dateInput: invalidInputValue,
            },
        });
    }, [invalidInputType, invalidInputValue]);

    const readableValue = React.useMemo(() => {
        if (!isValidValue) {
            return '';
        }

        return value.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
        });
    }, [isValidValue, value]);
    const humanPeriod = isValidValue ? getHumanPeriod(value, onlyDate) : '';
    const tooltip = useMemo(() => toLocaleDateTimeString(value), [value]);

    return {
        date: value,
        value: humanPeriod,
        accessibilityValue: prefix + readableValue,
        tooltip: prefix + tooltip,
    };
}

/**
 * Produces stable, bounded diagnostic data so repeated renders of the same invalid value only
 * report once and unexpected strings cannot create oversized Sentry events.
 */
function getInvalidDateInput(input: Date | string | number): { type: string; value: string } {
    return {
        type: input instanceof Date ? 'Date' : typeof input,
        value: String(input).slice(0, 200),
    };
}

function getHumanPeriod(value: Date, onlyDate: boolean) {
    const timestampNow = process.env.GITBOOK_PREDICTABLE_UI
        ? new Date(2021, 1, 1).getTime()
        : Date.now();

    const period = Math.max(1000, Math.abs(timestampNow - value.getTime()));
    const [years, months, days, hours, minutes] = [
        period / YEAR,
        period / MONTH,
        period / DAY,
        period / HOUR,
        period / MINUTE,
    ].map((n) => Math.floor(n));
    const isFuture = value.getTime() > timestampNow;

    const prefix = !onlyDate && isFuture ? 'in ' : '';
    const suffix = !onlyDate && !isFuture ? ' ago' : '';

    if (years >= 1) {
        return `${prefix}${years}yr${suffix}`;
    }
    if (months >= 1) {
        return `${prefix}${months}mo${suffix}`;
    }
    if (days >= 1) {
        return `${prefix}${days}d${suffix}`;
    }
    if (hours >= 1) {
        return `${prefix}${hours}h${suffix}`;
    }
    if (minutes >= 1) {
        return `${prefix}${minutes}m${suffix}`;
    }
    return 'now';
}
